import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Grid,
  Typography,
  TextField,
  Divider,
  Box,
} from "@mui/material";
import useUpdate from "../../../../api/useUpdate";
import { useTranslation } from "react-i18next";

const UpdateProcedureDialog = ({ open, onClose, procedure, token }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: procedure.name || "",
    description: procedure.description || "",
    cost: procedure.cost || null,
  });
  const [errors, setErrors] = useState({});
  const { handleUpdate, loading } = useUpdate("dental-procedure", token);

  const validateFields = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t("dental.nameRequired");
    if (formData.cost === "" || parseFloat(formData.cost) < 0)
      newErrors.cost = t("dental.invalidCost");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateFields()) return;

    handleUpdate(procedure.id, formData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: "absolute",
          top: "10px",
          margin: 0,
          borderRadius: "12px",
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <Box
        sx={{ padding: "20px 24px", backgroundColor: "#071952", color: "#fff" }}
      >
        <Typography variant="h5" fontWeight="bold">
          {t("dental.updateProcedure")}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
          {t("dental.updateProcedureDescription")}
        </Typography>
      </Box>

      <DialogContent
        sx={{
          padding: "32px",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label={t("dental.name")}
              fullWidth
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              error={!!errors.name}
              helperText={errors.name}
              sx={{
                "& .MuiInputBase-root": { backgroundColor: "#fff" },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("dental.description")}
              fullWidth
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              sx={{
                "& .MuiInputBase-root": { backgroundColor: "#fff" },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("dental.cost")}
              fullWidth
              type="number"
              value={formData.cost}
              onChange={(e) =>
                setFormData({ ...formData, cost: e.target.value })
              }
              error={!!errors.cost}
              helperText={errors.cost}
              sx={{
                "& .MuiInputBase-root": { backgroundColor: "#fff" },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          justifyContent: "flex-end",
          backgroundColor: "#f9f9f9",
          padding: "16px 24px",
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            color: "#071952",
          }}
        >
          {t("dental.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            marginLeft: "12px",
            backgroundColor: "#071952",
            ":hover": { backgroundColor: "#05173b" },
          }}
        >
          {loading ? t("dental.updating") : t("dental.update")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateProcedureDialog;
